/* eslint-disable no-loop-func */
import path from 'path';
import objectPath from 'object-path';
import Alert from 'react-s-alert';
import { observable, action, computed, when } from 'mobx';

import { AppManager, Variables, Input, routerStore } from 'core';
import { analytics } from 'core/analytics';

import { rootStore } from 'apps/store';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { ConfirmAction } from 'apps/modals/confirmAction';
import { FormStore } from 'libs/forms/app';
import { FileApp } from 'apps/file/app';
import FileAppManager from 'apps/file/appManager';
import { ReadmeApp } from 'apps/readme/app';
import {
  RepositoryModel,
  RepositoryApp,
  OperationApp,
} from 'apps/repository/app';
import { ContributionForm } from 'apps/repository/views/contributionForm';

import {
  RepoQuery,
  PollZipQuery,
  OperationMutation,
  CommitContributionMutation,
  CancelContributionMutation,
} from 'apps/repository/gql';

import { conf } from 'apps/repository/config.yaml';
import { SelectTemplateModal } from 'apps/readme/views/selectTemplate';
import { SelectSubtemplateParentModal } from 'apps/readme/views/selectSubtemplateParent';
import templateMap from 'apps/repository/templateHelper';

export class RepoAppManager extends AppManager {
  static registryName = 'RepoAppManager';
  @observable contributorsCount = 0;
  @observable hasError = false;
  @observable folderInputVisible = false;
  @observable newFolderName = '';
  @observable contributionTitle = '';
  @observable contributionDescription = '';
  @observable readme = null;

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('repository', RepositoryApp);
    this.AppRef('readme', ReadmeApp);
    this.readme = new ReadmeApp(this);
    this.variables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
      basepath: () => this.params.get('basepath') || '',
      version: () => this.params.get('contributionId') || '',
    });

    this.variables.onChange(
      ['basepath', 'version', 'space', 'slug'],
      this.NewRepoQuery,
    );

    clientStore.context.set('repoAppManager', this);
  }

  @computed get loading() {
    const query = this.queries.get('repo') || this.queries.get('version');
    return query?.loading;
  }

  @computed get atHead() {
    return !this.variables.version;
  }

  @action NewRepoQuery = () => {
    if (this.repoQuery) {
      this.repoQuery.disposer();
    }
    this.repoQuery = new RepoQuery(this, {
      queryKey: 'repo',
      onSysErr: this.onError,
      variables: this.variables,
    });
    this.repoQuery.process(
      result => RepositoryModel.Load(result),
      this.RepoLoad,
    );
    this.repoQuery.query();
  };

  @action NewZipQuery = () => {
    // If there's a polling query for the same repo running, don't create a new one
    if (
      objectPath.get(this.zipQuery, 'variables.slug') === this.variables.slug &&
      objectPath.get(this.zipQuery, 'variables.space') === this.variables.space
    )
      return;

    if (this.zipQuery) {
      this.zipQuery.disposer();
      this.zipQuery = null;
    }
    this.zipQuery = new PollZipQuery(this, {
      queryKey: 'pollZip',
      onSysErr: this.onError,
      pollInterval: 3000,
    });
    this.zipQuery.process(
      contributionUpstream =>
        contributionUpstream && contributionUpstream.zipArchiveUrl,
      this.ZipUpdate,
      { ignoreInitial: true },
    );
    this.zipQuery.query();
  };

  @action ZipUpdate = zipArchiveUrl => {
    if (
      objectPath.get(this.repository, 'contributionUpstream') &&
      zipArchiveUrl
    ) {
      this.repository.contributionUpstream.upstream.zipArchiveUrl =
        zipArchiveUrl;
      this.zipQuery.disposer();
      this.zipQuery = null;
    }
  };

  @action CatchError = error => {
    this.hasError = true;
    analytics.logError(error);
  };

  @action RepoLoad = repository => {
    if (!repository) return;
    this.repository = repository;

    // Set-up readme based on data from upstream
    const filename = 'README.md';
    const readmeRepoItem = this.repository.FindFileCWD(filename);
    const reference = readmeRepoItem?.reference || {
      origin: null,
      file: null,
    };

    if (!readmeRepoItem) {
      this.readme.setInitialized(false);
    }
    const projectId = this.repository.id;
    const dirname = this.variables.basepath;
    this.readme.dirname = dirname;
    this.readme.filename = filename;
    this.readme.projectId = projectId;
    this.readme.origin = reference.origin;
    this.readme.file = reference.file;
    this.readme.initialize();

    this.repository.localOperations = {};
    if (this.repository.conflicts.length > 0) {
      const spaceType = this.params.get('profile') ? 'profile' : 'initiative';
      routerStore.navigate(`${spaceType}.project.conflicts`, {
        [`${spaceType}`]:
          this.params.get('profile') || this.params.get('initiative'),
        slug: this.params.get('slug'),
      });
    }
    const { hasOpenContribution, contributionUpstream } = this.repository;
    if (!hasOpenContribution && !contributionUpstream?.zipArchiveUrl) {
      this.NewZipQuery();
    }
  };

  CancelDrafts() {
    if (!(clientStore.signedInProfile && this.repository)) return;
    const repoDraftKeys = Object.keys(localStorage).filter(key =>
      key.startsWith(
        [clientStore.signedInProfile.username, this.repository.id].join('-'),
      ),
    );
    repoDraftKeys.forEach(draftKey => {
      localStorage.removeItem(draftKey);
    });
  }

  @action CreateReadme = () => {
    analytics.sendEvent('created-readme');
    const dirname = this.variables.basepath;
    const title = dirname ? dirname.split('/').pop() : this.parent.project.name;
    this.readme.initialize({ content: `# ${title}`, sync: true });
    this.readme.Edit();
  };

  @computed get contributionCount() {
    return this.repository ? this.repository.contributionCount : 0;
  }

  @computed get contributorCount() {
    return this.repository ? this.repository.contributorCount : 0;
  }

  disposeWhen() {
    // FIXME - Check which routes are affected by this check
    return (
      routerStore.route.name.split('.').slice(0, 2).join('.') !==
      this.routeName.split('.').slice(0, 2).join('.')
    );
  }

  @action SetActiveTab = tabName => {
    this.activeTab = tabName;
  };

  @action SetFolderInputVisibility = visible => {
    if (visible !== this.folderInputVisible) {
      this.newFolderName = '';
      this.folderInputVisible = visible;
      this.folderForm = visible
        ? new FormStore(this, conf, 'newFolderForm')
        : null;
    }
  };

  @action CreateFolder = () => {
    if (this.folderForm.isValid) {
      const folderPath = path.join(this.variables.basepath, this.newFolderName);
      this.PerformOperation('ADD', { path: folderPath });
    }
    this.SetFolderInputVisibility(false);
  };

  @action ContributionForm = () => {
    this.form = new FormStore(this, conf, 'contributionForm');
    modalStore.SetModalComponent(ContributionForm, { manager: this });
  };

  @action onContributionError = error => {
    Alert.error(
      'Whoops! There was an error contributing. Please wait until all the files have finished uploading.',
    );
    analytics.logError(error);
  };

  @action CommitContribution = () => {
    const { form } = this;
    if (form.isValid) {
      const input = new Input({
        projectId: () => this.repository.id,
        title: () => this.contributionTitle,
        description: () => this.contributionDescription,
      });
      const options = {
        onSuccess: result => {
          form.clearDraft();
          this.contributionTitle = '';
          this.contributionDescription = '';
          analytics.sendEvent('contributed', {}, { ga: true, intercom: true });
          analytics.sendEvent(
            'Donate',
            { currency: 'EUR', value: 12 },
            { facebook: true },
          );
          this.CancelDrafts();
          if (result) {
            RepositoryModel.Load(result);
            rootStore.stores.processMonitor.NewProcessMonitorQuery();
          }
        },
        onUserErr: this.onContributionError,
      };
      const mutation = CommitContributionMutation.New(input, options);
      modalStore.Close();
      this.mutations.push(mutation);
      return mutation;
    }
    form.alert();
    return false;
  };

  @action CancelContribution = () => {
    const options = {
      onSuccess: result => {
        this.CancelDrafts();
        rootStore.stores.processMonitor.NewProcessMonitorQuery();
        RepositoryModel.Load(result);
      },
    };
    const input = new Input({
      projectId: () => this.repository.id,
    });
    const mutation = CancelContributionMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  @action ConfirmCancelContribution = () => {
    modalStore.SetModalComponent(ConfirmAction, {
      callBack: () => this.CancelContribution(),
      title: 'Reset your Contribution?',
      undo: true,
      text: 'This will discard all your local changes.',
      actionName: 'Accept',
    });
  };

  @action AddFiles = (
    files,
    options = { immediate: false, uploadCallback: () => {} },
  ) => {
    const { immediate, uploadCallback } = options;

    const fileManagers = Array.from(files).map(f => {
      const file = { ...f };
      const filePath = path.join(this.variables.basepath, file.fullPath);
      const { webkitRelativePath } = file;

      // If webkitRelativePath is present, it comes from a folder upload process
      // Then, we need to make sure that the internal folder structure exists
      if (webkitRelativePath) {
        let pathParts = webkitRelativePath.split('/');
        pathParts = pathParts.filter(p => p !== file.name);
        for (let i = 0; i < pathParts.length; i += 1) {
          const part = pathParts[i];
          let accumulatedPath = '';

          if (i > 0) {
            for (let p = 0; p < i; p += 1) {
              accumulatedPath = path.join(accumulatedPath, pathParts[p]);
            }
          }
          accumulatedPath = path.join(accumulatedPath, part);

          // Calculate the path folder from the current basepath
          const pathFromCurrentPosition = path.join(
            this.variables.basepath,
            accumulatedPath,
          );

          this.WIPoperation('ADD', { path: pathFromCurrentPosition });
        }
        // Update the internal file fullpath from the current folder
        file.fullPath = webkitRelativePath;
      }

      const fileManager = new FileAppManager(null, {
        formTarget: this.parent.project,
        projectPath: webkitRelativePath || filePath,
      });

      if (immediate) {
        clientStore.uploader.FastTrackFile(fileManager, file, uploadCallback);
      } else {
        clientStore.uploader.QueueFile(fileManager, file, uploadCallback);
      }
      this.WIPoperation('ADD', {
        path: webkitRelativePath || filePath,
        file: fileManager.file,
      });
      return { fileManager, file };
    });
    // FIXME - Change from single 'when' to multiple 'whens' (or promises)
    when(
      () =>
        fileManagers.filter(({ fileManager }) => !fileManager.fileModelCreated)
          .length === 0,
      () => this.ApplyAddFiles(fileManagers, this.variables.basepath),
    );
  };

  @action ApplyAddFiles = (fileManagers, basepath) => {
    fileManagers.forEach(({ fileManager, file }) => {
      const filePath = path.join(basepath, file.fullPath);
      this.PerformOperation('ADD', { path: filePath, file: fileManager.file });
    });
  };

  @action DeleteFile = fileInfo => {
    const { reference } = fileInfo;
    // eslint-disable-next-line
    const { path } = reference;
    this.PerformOperation('REMOVE', { path, file: reference });
  };

  @action MoveFile = (fileInfo, destinationInfo) => {
    const destination = destinationInfo.reference;
    if (!destination.isFolder) return;
    const file = fileInfo.reference;
    const oldPath = file.path;
    const newPath = path.join(destination.path, file.filename);
    this.PerformOperation('RENAME', {
      oldPath,
      path: newPath,
      file: file.file,
    });
  };

  @action RenameFile = (fileInfo, newName) => {
    const { reference } = fileInfo;
    const oldPath = reference.path;
    const newPath = path.join(reference.dirname, newName);
    if (oldPath !== newPath) {
      this.PerformOperation('RENAME', {
        oldPath,
        path: newPath,
        file: reference.file,
      });
    }
    reference.SetRenaming(false);
  };

  @action ResetFile = fileInfo => {
    const { reference } = fileInfo;
    // eslint-disable-next-line no-shadow
    const { path } = reference;
    const input = new Input({
      projectId: () => this.repository.id,
      path: () => path,
    });
    const options = {
      onSuccess: result => RepositoryModel.Load(result),
    };
    const mutation = CancelContributionMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  // eslint-disable-next-line
  @action WIPoperation = (opType, { path, oldPath, file }) => {
    const wipOp = new OperationApp();
    wipOp.opType = 'WIP';
    wipOp.path = path;
    wipOp.oldPath = oldPath;
    wipOp.file = file;
    this.repository.localOperations[path] = wipOp;
  };

  // eslint-disable-next-line
  @action PerformOperation = (opType, { path, oldPath, file }) => {
    /* Supported operations + params
      - ADD: path, file
      - REMOVE: path
      - RENAME: oldPath, newPath
      - UPDATE: path, file
    There's a RESET operation, which uses a different mutation
    and thus is handled by a separate function */
    const input = new Input({
      projectId: () => this.repository.id,
      opType: () => opType,
      path: () => path,
      oldPath: () => oldPath,
      fileId: () => file && file.id,
    });
    const options = {
      onSuccess: result => {
        analytics.sendEvent('uploaded-file');
        RepositoryModel.Load(result);
        const { processMonitor } = rootStore.stores;
        if (processMonitor) {
          processMonitor.NewProcessMonitorQuery();
        }
      },
    };

    const mutation = OperationMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  fixCWD() {
    const { repository, cwdFiles } = this;
    if (
      repository &&
      !repository.hasOpenContribution &&
      cwdFiles.length === 0
    ) {
      let projectRoute = `${
        routerStore.route.name.split('.project')[0]
      }.project.files`;
      const { params } = routerStore.route;
      if (this.variables.version) {
        projectRoute = `${
          routerStore.route.name.split('.project')[0]
        }.project.version.files`;
      }
      delete params.basepath;
      routerStore.router.navigate(projectRoute, params);
    }
  }

  @action OpenReadmeTemplates = () => {
    this.readme.init({ content: '', sync: true });
    modalStore.SetModalComponent(SelectTemplateModal, { readmeApp: this });
  };

  @action OpenSubtemplates = (parentTemplate, editing = false) => {
    let templateToUse = parentTemplate;

    /* Since we are not storing the original template, for the moment we assume 
    it will be the osh one. 
    In order to find if the subsections are present, we check if there is
    any section in the readme, whose title is the same as the one defined
    in the config.yaml file */

    if (!parentTemplate) {
      templateToUse = conf.templates.oshDocumentationGuidelines;
    }
    const filledSections = []; // Contains the ids of the already filled sections
    if (editing) {
      // Find the already filled sections

      const { sections } = conf.templates.oshDocumentationGuidelines;

      Object.keys(conf.templates.oshDocumentationGuidelines.sections).forEach(
        templateId => {
          const readmeHasTitle = this.readme.hasSectionWithTitle(
            sections[templateId].title,
          );
          if (readmeHasTitle) {
            filledSections.push(templateId);
          }
        },
      );
    }

    modalStore.SetModalComponent(SelectSubtemplateParentModal, {
      readmeApp: this,
      parentTemplate,
      subsections: templateToUse.sections,
      clickConfirm: this.OnSubtemplateConfirm,
      subtemplateSelected: this.OnSubtemplateSelected,
      userJourneysData: templateToUse.userJourneys,
      filledSections,
    });
  };

  @action getDefaultTemplate = () => {
    return null;
  };

  getContentOfFile = async (templateid, filename) => {
    const templateFile = new FileApp(null, {
      id: `nr-readme-template-${filename}`,
      permalink: templateMap[filename],
    });
    await templateFile.readAsText();

    return { templateid, content: templateFile.fileAsText };
  };

  getFullContent = async templatesInfo => {
    const filesArray = [];

    const templateKeys = Object.keys(templatesInfo);

    for (let i = 0; i < templateKeys.length; i += 1) {
      const templateId = templateKeys[i];

      if (templatesInfo[templateId].isSelected === true) {
        const templateInfo = templatesInfo[templateId];
        const templateFile = new FileApp(null, {
          id: `readme-template-${templateId}`,
          permalink: templateMap[templateInfo.file],
        });
        filesArray.push(templateFile);
      }
    }

    // Iterate through the files and generate the content
    let content = '';

    for (let i = 0; i < filesArray.length; i += 1) {
      const file = filesArray[i];

      // eslint-disable-next-line no-await-in-loop
      await file.readAsText();
      // eslint-disable-next-line no-loop-func
      content += file.fileAsText;
      content += '\n';
    }

    return content;
  };

  processAlreadyExistingSubtemplates = (
    templatesInfo,
    previouslyFilledSections,
  ) => {
    /* We need to extract the individual sectionApps for each subsection
         and in order to do so, we need to know the extension in the array
         of each of the templates. 
         */
    const sectionIndexArray = [];
    this.readme.sectionApps.forEach((sectionapp, index) => {
      if (previouslyFilledSections.includes(sectionapp.title.toLowerCase())) {
        sectionIndexArray.push({
          section: sectionapp.title.toLowerCase(),
          index,
        });
      }
    });

    /* Once we have the information (e.g. The "USE" section goes from 
             index 12 to 23), we extract the sectionApps from the readmes */
    const sectionAppsForEachSection = {};
    sectionIndexArray.forEach((sectionindex, i) => {
      const sectionId = sectionindex.section;
      const indexForSection = sectionindex.index;

      // Depending on the position in the array, we need to go...

      let apps;
      // from 0th element to the start of the second section
      if (indexForSection === 0) {
        apps = this.readme.sectionApps.slice(0, sectionIndexArray[i + 1].index);
        // from the start of the last section to the end of the array
      } else if (i === sectionIndexArray.length - 1) {
        apps = this.readme.sectionApps.slice(
          indexForSection,
          this.readme.sectionApps.length,
        );
      } else {
        // and from the index of the section to the beginning of the next one
        apps = this.readme.sectionApps.slice(
          indexForSection,
          sectionIndexArray[i + 1].index,
        );
      }
      sectionAppsForEachSection[sectionId] = apps;
    });

    const newSectionAppsMap = {};
    const promises = [];
    // Now, we iterate trough the templates information.
    Object.keys(templatesInfo).forEach(key => {
      const sectionInfo = templatesInfo[key];

      // If the section appears as "selected" and we already have the sections...
      if (sectionInfo.isSelected && previouslyFilledSections.includes(key)) {
        newSectionAppsMap[key] = sectionAppsForEachSection[key];
      } else if (
        sectionInfo.isSelected &&
        !previouslyFilledSections.includes(key)
      ) {
        // Otherwise, we need to actually create those sections
        promises.push(this.getContentOfFile(key, sectionInfo.file));
      }
    });

    Promise.all(promises).then(contentArray => {
      contentArray.forEach(info => {
        // Once we have finished reading the file for each template,
        // create a new temporal readme and "steal" its sections

        const newreadme = new ReadmeApp({});

        newreadme.initialize({ content: info.content });
        newSectionAppsMap[info.templateid] = [...newreadme.sectionApps]; // To de-proxify
      });

      // Finally, we do a last iteration through the templates information
      // (we have now all the information about the sections) and override the
      // sectionApps of the readme. By doing this, we ensure to keep the same
      // order as defined in the yaml file
      const finalOrderedSectionApps = [];
      Object.keys(templatesInfo).forEach(key => {
        const sectionInfo = templatesInfo[key];
        if (sectionInfo.isSelected) {
          const apps = newSectionAppsMap[key];
          finalOrderedSectionApps.push(...apps);
        }
      });
      // console.log(finalOrderedSectionApps);
      // Override the apps
      this.readme.setSections(finalOrderedSectionApps);
      this.readme.Edit();
      modalStore.ClearComponent();
    });
  };

  @action OnSubtemplateConfirm = (templatesInfo, previouslyFilledSections) => {
    /* TemplatesInfo is the original JSON Object, which is constant 
       PreviouslyFilledSections contains a list of those sections that are already
       in the readme */

    if (!previouslyFilledSections || previouslyFilledSections.length === 0) {
      // If there are no old templates, we can do the old way
      // Just generate the whole readme file with the selection
      this.getFullContent(templatesInfo).then(content => {
        this.readme.initialize({ content, sync: true });
        this.readme.Edit();

        modalStore.ClearComponent();
      });
    } else {
      this.processAlreadyExistingSubtemplates(
        templatesInfo,
        previouslyFilledSections,
      );
    }
  };

  @action ChooseTemplate = selectedTemplate => {
    const templateFile = new FileApp(null, {
      id: `readme-template-${selectedTemplate.title}`,
      permalink: templateMap[selectedTemplate.file],
    });
    this.readme.file = templateFile;
    this.readme.initialize();

    this.readme.Edit();
  };
}
